import React from 'react';
import { PublicReviewSchema, type PublicReview } from '@xshop/shared';
import { fetchApi } from '../../api/client';
import { isThemePreview } from '../../api/previewVisit';
import { ElementRenderProvider, useElementBox, useElementRender, type ElementComponent } from './elementRenderContext';
import { boolProp, choiceProp, numberProp, textProp, type ElementProps } from './elementProps';
import { reviewFactsOf, type ReviewFacts } from './reviewFacts';
import { useStoreRead } from './useStoreRead';
import { useProduct } from './productContext';
import { CatalogNote, CatalogSkeleton, columnsStyle } from './catalogCards';
import { CatalogSlider, SLIDER_ARROWS } from './CatalogSlider';
import { ReviewExtras } from './ReviewExtras';
import { ReviewWriter } from './ReviewWriter';
import { REVIEW_WRITE_WORDS } from './reviewWriteWords';
import './elementReviewList.css';

// The store's own approved reviews, each drawn with the card the merchant designed inside this element:
// the reviewer, their words, the stars and the product. A theme preview and the builder show the theme's
// samples while a store has none; a live store with none shows no section at all, because a review a
// shopper cannot trust is worse than no reviews — the section around an empty list hides itself. A product
// page's list is the exception: it asks for the first review, and under every list of a product's reviews
// sits the button a shopper writes one with.

/** The most reviews a row may draw, matching what the public read allows. */
const MAX_REVIEWS = 12;

// Reads the newest approved reviews, of one product when given, refusing any row that is not the shape a card reads.
async function readReviews(limit: number, productId: string | null): Promise<ReviewFacts[]> {
  const rows = await fetchApi<unknown[]>(`/reviews/storefront?limit=${limit}${productId ? `&productId=${encodeURIComponent(productId)}` : ''}`);
  return rows.flatMap((row) => { const parsed = PublicReviewSchema.safeParse(row); return parsed.success ? [reviewFactsOf(parsed.data as PublicReview)] : []; });
}

// Reads the theme's sample reviews, which only a preview and the builder ever draw.
function sampleReviews(props: ElementProps, limit: number): ReviewFacts[] {
  if (!boolProp(props, 'sampleContent') || !Array.isArray(props.samples)) return [];
  return props.samples.slice(0, limit).flatMap((raw) => {
    if (!raw || typeof raw !== 'object' || typeof raw.author !== 'string' || typeof raw.body !== 'string') return [];
    // Reads one of the sample's words, or nothing when the theme left it out.
    const text = (key: string) => (typeof raw[key] === 'string' ? raw[key] as string : '');
    return [{ author: raw.author, body: raw.body, title: '', rating: Number(raw.rating ?? 5), verified: true, image: text('image'),
      productTitle: text('productTitle'), productHref: '/collections/all', productImage: text('productImage'), audio: '', video: '', reply: '' }];
  });
}

// Draws one card under one review's facts, so its bound parts say that review's words.
const ReviewScope: React.FC<{ review: ReviewFacts; children: React.ReactNode }> = ({ review, children }) => {
  const context = useElementRender();
  const base = context.store ?? { storeName: '', logoUrl: '', currency: 'MAD', moneyLocale: 'fr-MA' as const, cartCount: 0, cartTotal: 0, product: null };
  return <ElementRenderProvider value={{ ...context, store: { ...base, review } }}>{children}</ElementRenderProvider>;
};

// Names the product whose reviews a list shows: null for the whole shop, undefined while the page's product is still coming.
function useReviewedProduct(props: ElementProps): string | null | undefined {
  const product = useProduct();
  if (!boolProp(props, 'ofProduct')) return null;
  return product && !product.id.startsWith('sample-') ? product.id : undefined;
}

// Names the product a shopper may review from this list: the page's product, a stand-in while designing, or none.
function writableProduct(props: ElementProps, productId: string | null | undefined, designing: boolean): string | null {
  if (!boolProp(props, 'ofProduct') || boolProp(props, 'hideWriteReview')) return null;
  return typeof productId === 'string' ? productId : designing ? '' : null;
}

// Draws the store's approved reviews with the element's own card, its samples while designing, or nothing.
export const ReviewListElement: ElementComponent = ({ node, children }) => {
  const box = useElementBox(node);
  const { editing, store } = useElementRender();
  const limit = Math.min(MAX_REVIEWS, Math.max(1, numberProp(node.props, 'limit', 6)));
  const productId = useReviewedProduct(node.props);
  const read = useStoreRead(productId === undefined ? null : `reviews:${limit}:${productId ?? 'all'}`, () => readReviews(limit, productId ?? null));
  const own = read.data ?? [];
  const designing = editing || isThemePreview();
  const rows = own.length > 0 ? own : designing ? sampleReviews(node.props, limit) : [];
  const className = [box.className, 'xe-reviews'].filter(Boolean).join(' ');
  const writeFor = writableProduct(node.props, productId, designing);
  const words = REVIEW_WRITE_WORDS[store?.language ?? 'en'];
  // 1. A product with no reviews asks for the first; any other empty list marks itself so its section hides.
  if (rows.length === 0 && read.status !== 'loading') {
    if (writeFor === null) return <div {...box} className={className} data-empty="" />;
    return <div {...box} className={`${className} xe-reviews-none`}><p>{words.none}</p><ReviewWriter productId={writeFor} words={words} /></div>;
  }
  // 2. The element's own box is the row of cards, so a slider's arrows can stand level with the heading above it.
  const track = { ...box, className: `${className} xe-reviews-track`, style: columnsStyle(numberProp(node.props, 'columns', 3)) };
  const sliding = textProp(node.props, 'layout') === 'carousel' && rows.length > 1;
  return (
    <>
      <CatalogSlider on={sliding} editing={editing} track={track} arrows={choiceProp(node.props, 'arrows', SLIDER_ARROWS, 'above')}>
        {read.status === 'loading' && rows.length === 0 ? <CatalogSkeleton count={3} /> : null}
        {rows.map((review, at) => (
          <div key={`${review.author}-${at}`} className="xe-reviews-item">
            <ReviewScope review={review}>{children}</ReviewScope>
            {boolProp(node.props, 'ofProduct') && <ReviewExtras review={review} words={words} />}
          </div>
        ))}
        {editing && own.length === 0 && read.status !== 'loading' ? <CatalogNote>Your approved reviews appear here; these samples show only while you have none.</CatalogNote> : null}
      </CatalogSlider>
      {writeFor !== null && <div className="xe-reviews-writer"><ReviewWriter productId={writeFor} words={words} /></div>}
    </>
  );
};
